<section class="charter-page">
  <!-- Caminho padrão do Cognito (app-breadcrumb) com o seletor de empresa como último item. -->
  <header class="top-bar">
    <app-breadcrumb class="top-bar__crumbs" [home]="breadcrumbHome" [items]="breadcrumbs" />
    <span class="top-bar__sep" aria-hidden="true">
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path
          d="M4.387 13a.78.78 0 0 1-.554-1.338l4.672-4.672-4.672-4.672a.78.78 0 0 1 1.108-1.108l5.226 5.226a.78.78 0 0 1 0 1.108l-5.226 5.226a.77.77 0 0 1-.554.23Z"
          fill="currentColor" />
      </svg>
    </span>
    <app-charter-company-switcher
      class="top-bar__switcher"
      [companyId]="state.companyId()"
      [companyName]="state.companyName()"
      [companyCnpj]="state.companyCnpj()" />
  </header>

  <!-- Segunda linha: resumo à esquerda e ações à direita. A primeira linha (caminho) deixa o canto direito livre
       para o avatar e o balão de feedback da plataforma, que flutuam sobre o topo. -->
  <div class="sub-bar">
    <div class="sub-bar__summary">
      @if (!state.loading() && !state.loadFailed() && state.charter(); as charter) {
        @if (charter.status !== 'empty') {
          <app-charter-summary-strip
            [charter]="charter"
            [currentAct]="state.currentAct()"
            [processingCount]="state.processingActs().length"
            (reviewRequested)="activeTab.set(1)" />
        }
      }
    </div>
    <div class="sub-bar__actions">
      <button
        type="button"
        class="bar-btn"
        title="Abrir o Data Room da empresa"
        (click)="state.openDataRoom()">
        <i class="pi pi-folder-open" aria-hidden="true"></i>
        <span class="bar-btn__label">Data Room</span>
      </button>
      @if (state.currentAct(); as current) {
        <button
          type="button"
          class="bar-btn"
          [title]="'Abrir ' + current.label + ': ' + current.documentName"
          (click)="openCurrentDocument()">
          <i class="pi pi-file" aria-hidden="true"></i>
          <span class="bar-btn__label">Documento vigente</span>
        </button>
      }
      @if (state.canEdit() && !state.isEmpty()) {
        <button
          type="button"
          class="bar-btn"
          [disabled]="state.reprocessing()"
          title="O Cog lê de novo os contratos sociais, alterações, estatutos e atas da empresa"
          (click)="state.reprocessCompany()">
          <i
            class="pi"
            [class.pi-refresh]="!state.reprocessing()"
            [class.pi-spin]="state.reprocessing()"
            [class.pi-sync]="state.reprocessing()"
            aria-hidden="true"></i>
          <span class="bar-btn__label">{{
            state.reprocessing() ? 'Enviando...' : 'Reprocessar'
          }}</span>
        </button>
      }
    </div>
  </div>

  <!-- Com vigente: aviso discreto dos documentos novos do Data Room que o Cog ainda não leu. -->
  @if (!state.loading() && !state.loadFailed() && state.charter()?.status !== 'empty') {
    @if (state.dataRoomPending(); as pending) {
      <app-charter-data-room-pending
        variant="compact"
        [pending]="pending"
        [canEdit]="state.canEdit()"
        [processing]="state.reprocessing()"
        [sent]="state.processingRequested()"
        [openingDocumentId]="state.openingDocumentId()"
        (process)="state.processDataRoomDocuments()"
        (openDocument)="state.openDocument($event)"
        (openDataRoom)="state.openDataRoom()" />
    }
  }

  <app-tab-view
    class="tabs"
    [tabViewItems]="tabs()"
    [tabIndex]="activeTab()"
    theme="line"
    (sendTabViewActiveIndex)="activeTab.set($event)" />

  @if (activeTab() !== registryTab) {
    @if (state.loading()) {
      <div class="state"><app-cog-loading-spinner /></div>
    } @else if (state.loadFailed()) {
      <div class="state">
        <i class="pi pi-exclamation-circle state__icon state__icon--danger" aria-hidden="true"></i>
        <strong>Não foi possível carregar o societário desta empresa.</strong>
        <app-button
          [label]="'Tentar de novo'"
          theme="outline-grey"
          [noMargin]="true"
          (sendButtonClick)="state.load()" />
      </div>
    } @else if (state.isEmpty() && state.dataRoomPending(); as pending) {
      <!-- Sem vigente, mas com documentos na pasta de contrato social que o Cog ainda não leu. -->
      <app-charter-data-room-pending
        variant="empty"
        [pending]="pending"
        [canEdit]="state.canEdit()"
        [processing]="state.reprocessing()"
        [openingDocumentId]="state.openingDocumentId()"
        (process)="state.processDataRoomDocuments()"
        (openDocument)="state.openDocument($event)"
        (openDataRoom)="state.openDataRoom()" />
    } @else if (state.isEmpty()) {
      <div class="state state--empty">
        <span class="state__badge"><i class="pi pi-search" aria-hidden="true"></i></span>
        <strong class="state__title">Nenhum contrato social lido ainda</strong>
        <p class="state__text">
          Envie o contrato social e as alterações (ou o estatuto e as atas, na S.A.) para o Data
          Room da empresa. Quando o Cog classificar o documento, ele lê os sócios, o capital e a
          administração sozinho.
        </p>
        <div class="state__actions">
          <app-button
            [icon]="'pi pi-upload'"
            [label]="'Enviar para o Data Room'"
            theme="darkblue"
            [noMargin]="true"
            (sendButtonClick)="state.openDataRoom()" />
          @if (state.canEdit()) {
            <app-button
              [icon]="'pi pi-refresh'"
              [label]="'Ler documentos já enviados'"
              theme="outline-grey"
              [noMargin]="true"
              [isLoading]="state.reprocessing()"
              [loadingText]="'Enviando...'"
              title="Para contratos que já estavam no Data Room antes desta leitura existir"
              (sendButtonClick)="state.reprocessCompany()" />
          }
        </div>
        <p class="state__hint">
          Enquanto isso, os sócios da Receita Federal continuam na aba "Receita e Mapa societário".
        </p>
      </div>
    } @else if (state.isWaitingFirstRead()) {
      <div class="state">
        <app-cog-loading-spinner />
        @if (state.processingActs().length > 0) {
          <strong>O Cog está lendo o contrato social.</strong>
          <span class="state__text">
            {{ state.processingActs().length }}
            {{ state.processingActs().length === 1 ? 'documento' : 'documentos' }} em leitura. A
            tela se atualiza sozinha.
          </span>
        } @else {
          <strong>O Cog recebeu os documentos do Data Room.</strong>
          <span class="state__text">
            Primeiro ele classifica os que ainda não têm tipo; a leitura do contrato social começa
            em seguida. A tela se atualiza sozinha.
          </span>
        }
      </div>
    } @else if (state.charter(); as charter) {
      <div class="content">
        @switch (activeTab()) {
          @case (1) {
            <app-charter-acts-timeline
              [acts]="state.acts()"
              [timeline]="charter.timeline"
              [missingActs]="charter.missingActs"
              [currentActId]="state.currentAct()?.actId ?? null"
              [isInternal]="state.isInternal()"
              [isAdmin]="state.isAdmin()"
              [openingDocumentId]="state.openingDocumentId()"
              [highlightedActId]="highlightedActId()"
              (openInDataRoom)="state.openDocument($event)" />
          }
          @case (2) {
            <app-charter-former-card
              [formerHolders]="charter.formerHolders"
              [formerAdministrators]="charter.governance.formerAdministrators"
              [legalNature]="charter.legalNature"
              [unitKind]="charter.capital.unitKind"
              [currency]="charter.capital.currency" />
          }
          @default {
            <app-charter-current-view [charter]="charter" />
          }
        }
      </div>
    }
  } @else {
    <div class="registry">
      @if (state.companyCnpj(); as cnpj) {
        <app-deal-registry-panel [cnpj]="cnpj" [showNetwork]="false" />
        <!-- Mapa compartilhado com Deals; aqui abre na visão Controle, a que mostra as holdings. -->
        <app-registry-network-card [cnpj]="cnpj" [initialView]="'mapa'" [showHoldingBadge]="true" />
      } @else {
        <div class="state">
          <strong>Sem CNPJ cadastrado para consultar a Receita Federal.</strong>
        </div>
      }
    </div>
  }

  <app-charter-document-viewer (openInDataRoom)="state.openDocument($event)" />
</section>
